import React, { useEffect, useRef, useState } from 'react';
import { Package } from 'lucide-react';
import { GoHoverTooltip } from '../../go-config/components/GoHoverTooltip';
import { fetchTechnicianPanelOsDetail } from '../services/technicianPanelApi';
import TechnicianPanelOsFotosModal from './TechnicianPanelOsFotosModal';
import TechnicianPanelPartStockPopover from './TechnicianPanelPartStockPopover';
import OpenOsV2EquipmentHistoryOverlay from '../../new-os/components/OpenOsV2EquipmentHistoryOverlay';

function formatWindowFallback(os) {
  const ini = os.horaIni || '—';
  const fim = os.horaFim || '—';
  if (!os.horaIni && !os.horaFim) return 'aguardando check-in';
  return `${ini} · ${fim}`;
}

function PartRow({ part, pending }) {
  const stockBtnRef = useRef(null);
  const [stockOpen, setStockOpen] = useState(false);
  const name = (part.name && String(part.name).trim()) || '';

  return (
    <div className={`tpm-os-drawer-part${pending ? ' tpm-os-drawer-part--pending' : ''}`}>
      <span
        className={`tpm-os-drawer-part-code${pending ? ' tpm-os-drawer-part-code--pending' : ''}`}
      >
        {part.code || '—'}
      </span>
      <GoHoverTooltip text={name || undefined} align="start" wrap className="tpm-os-drawer-part-ref-tip">
        <span className="tpm-os-drawer-part-ref">{part.ref || '—'}</span>
      </GoHoverTooltip>
      {part.code ? (
        <>
          <GoHoverTooltip text="Informações de estoque" align="center" className="go-hover-tip-wrap--contents">
            <button
              ref={stockBtnRef}
              type="button"
              className={`tpm-os-drawer-stock-btn${stockOpen ? ' is-open' : ''}`}
              aria-label="Informações de estoque"
              onClick={() => setStockOpen((v) => !v)}
            >
              <Package size={13} strokeWidth={2.2} aria-hidden />
            </button>
          </GoHoverTooltip>
          <TechnicianPanelPartStockPopover
            open={stockOpen}
            onClose={() => setStockOpen(false)}
            productCode={part.code}
            productName={name || part.ref || part.code}
            anchorRef={stockBtnRef}
          />
        </>
      ) : null}
      <span className="tpm-os-drawer-part-qty">{part.qty != null ? `${part.qty}x` : '—'}</span>
    </div>
  );
}

/**
 * Drawer de detalhe da OS — layout do HTML Painel do Tecnico_detail.
 * Carrega fechamento, peças e fotos via API do painel.
 */
export default function TechnicianPanelMockOsDrawer({ os, onClose }) {
  const [detailPayload, setDetailPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFotos, setShowFotos] = useState(false);
  const [histOpen, setHistOpen] = useState(false);

  useEffect(() => {
    if (!os?.os) {
      setDetailPayload(null);
      setShowFotos(false);
      setHistOpen(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setDetailPayload(null);
    setShowFotos(false);
    setHistOpen(false);
    (async () => {
      try {
        const data = await fetchTechnicianPanelOsDetail(os.os);
        if (!cancelled) setDetailPayload(data);
      } catch {
        if (!cancelled) setDetailPayload(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [os?.os]);

  useEffect(() => {
    if (!os) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (histOpen) {
        e.stopPropagation();
        setHistOpen(false);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [os, histOpen]);

  if (!os) return null;

  const d = detailPayload?.detail || null;
  const meta = os.meta || { color: '#6d54e8', bg: '#faf9ff', border: '#e1ddfb' };
  const condNome = d?.condNome || os.condNome || 'Sem condição';
  const city = (d?.city && d.city.trim()) || '—';
  const district = (d?.district && d.district.trim()) || '—';
  const patrimony = (d?.patrimony && d.patrimony.trim()) || '—';
  const serial = (d?.serial && d.serial.trim()) || '';
  const client = (d?.client && d.client.trim()) || os.client || '—';
  const equip = os.equip || '—';
  const windowText = d?.window || formatWindowFallback(os);
  const laudo = d?.laudo && d.laudo.trim() ? d.laudo : loading ? 'Carregando…' : '—';
  const counters =
    d?.counters ||
    [
      { k: 'A4 P&B', v: '—', empty: true },
      { k: 'A4 cor', v: '—', empty: true },
      { k: 'Dig.', v: '—', empty: true },
      { k: 'A3 P&B', v: '—', empty: true },
      { k: 'A3 cor', v: '—', empty: true },
    ];
  const pendingParts = detailPayload?.pendingParts || [];
  const usedParts = detailPayload?.usedParts || [];
  const fotos = detailPayload?.fotos || [];
  const hasFotos = fotos.length > 0;

  return (
    <>
      <OpenOsV2EquipmentHistoryOverlay
        isOpen={histOpen && !!serial}
        onClose={() => setHistOpen(false)}
        serie={serial}
        modelo={equip}
        patrimonio={patrimony}
      />

      <aside className="tpm-os-drawer" data-tpm="os-drawer" role="dialog" aria-label={`OS ${os.os}`}>
        <div className="tpm-os-drawer-head">
          <div className="tpm-os-drawer-title-row">
            <span className="tpm-os-drawer-os">#{os.os}</span>
            <div className="tpm-flex" />
            {hasFotos && (
              <button
                type="button"
                className="tpm-os-drawer-chip-link tpm-os-drawer-fotos-btn"
                onClick={() => setShowFotos(true)}
              >
                fotos
              </button>
            )}
            <button type="button" className="tpm-os-drawer-close" aria-label="Fechar" onClick={onClose}>
              <svg width="11" height="11" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.6" fill="none">
                <path d="M5 5l14 14M19 5L5 19" />
              </svg>
            </button>
          </div>
          <span className="tpm-os-drawer-loc">
            {city} · {district}
          </span>
        </div>

        <div className="tpm-os-drawer-body">
          <div className="tpm-os-drawer-meta">
            <div className="tpm-os-drawer-field">
              <span className="tpm-os-drawer-field-k">Patrimônio</span>
              <span className="tpm-os-drawer-patrimony-row">
                <span className="tpm-os-drawer-patrimony">{patrimony}</span>
                {serial ? <span className="tpm-os-drawer-serial">{serial}</span> : null}
                <GoHoverTooltip
                  text={
                    serial
                      ? 'Ver histórico de serviços do equipamento'
                      : 'Número de série indisponível'
                  }
                  align="center"
                  wrap
                  className="go-hover-tip-wrap--contents"
                >
                  <button
                    type="button"
                    className={`tpm-os-drawer-hist-btn${histOpen ? ' is-open' : ''}`}
                    aria-label={
                      serial
                        ? 'Ver histórico de serviços do equipamento'
                        : 'Número de série indisponível'
                    }
                    disabled={!serial}
                    onClick={() => serial && setHistOpen((v) => !v)}
                  >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <path d="M3 12a9 9 0 1 0 3-6.7" />
                      <path d="M3 4v4h4" />
                      <path d="M12 8v4.5l3 1.8" />
                    </svg>
                    histórico de serviços
                  </button>
                </GoHoverTooltip>
              </span>
            </div>
            <div className="tpm-os-drawer-field">
              <span className="tpm-os-drawer-field-k">Cliente</span>
              <span className="tpm-os-drawer-field-v">{client}</span>
            </div>
            <div className="tpm-os-drawer-field">
              <span className="tpm-os-drawer-field-k">Modelo</span>
              <span className="tpm-os-drawer-field-v tpm-os-drawer-field-v--muted">{equip}</span>
            </div>
            <div className="tpm-os-drawer-field">
              <span className="tpm-os-drawer-field-k">Horários de atendimento</span>
              <span className="tpm-os-drawer-window-row">
                <span className="tpm-os-drawer-window">{windowText}</span>
                <span className="tpm-os-drawer-dur-chip">{os.dur || '—'}</span>
              </span>
            </div>
            <div className="tpm-os-drawer-field">
              <span className="tpm-os-drawer-field-k">Condição</span>
              <span
                className="tpm-os-drawer-cond"
                style={{ background: meta.bg, borderColor: meta.border }}
              >
                <span className="tpm-os-drawer-cond-dot" style={{ background: meta.color }} />
                <span className="tpm-os-drawer-cond-label" style={{ color: meta.color }}>
                  {condNome}
                </span>
              </span>
            </div>
          </div>

          <div className="tpm-os-drawer-section">
            <span className="tpm-os-drawer-section-label">Texto do laudo</span>
            <div className="tpm-os-drawer-laudo">{laudo}</div>
          </div>

          <div className="tpm-os-drawer-section">
            <span className="tpm-os-drawer-section-label">Contadores</span>
            <div className="tpm-os-drawer-counters">
              {counters.map((c) => (
                <div key={c.k} className="tpm-os-drawer-counter">
                  <span className="tpm-os-drawer-counter-k">{c.k}</span>
                  <span
                    className={`tpm-os-drawer-counter-v${c.empty ? ' is-empty' : ''}`}
                  >
                    {c.v}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="tpm-os-drawer-section">
            <div className="tpm-os-drawer-parts-head">
              <span className="tpm-os-drawer-parts-dot tpm-os-drawer-parts-dot--pending" />
              <span className="tpm-os-drawer-section-label">Peças pendentes</span>
              <div className="tpm-flex" />
              <span className="tpm-os-drawer-parts-count tpm-os-drawer-parts-count--pending">
                {pendingParts.length}
              </span>
            </div>
            <div className="tpm-os-drawer-parts tpm-os-drawer-parts--pending">
              {pendingParts.length === 0 ? (
                <div className="tpm-os-drawer-parts-empty">Nenhuma peça pendente</div>
              ) : (
                pendingParts.map((p) => <PartRow key={`${p.code}-${p.ref}`} part={p} pending />)
              )}
            </div>
          </div>

          <div className="tpm-os-drawer-section">
            <div className="tpm-os-drawer-parts-head">
              <span className="tpm-os-drawer-parts-dot tpm-os-drawer-parts-dot--used" />
              <span className="tpm-os-drawer-section-label">Peças trocadas</span>
              <div className="tpm-flex" />
              <span className="tpm-os-drawer-parts-count tpm-os-drawer-parts-count--used">
                {usedParts.length}
              </span>
            </div>
            <div className="tpm-os-drawer-parts">
              {usedParts.length === 0 ? (
                <div className="tpm-os-drawer-parts-empty">Nenhuma peça trocada</div>
              ) : (
                usedParts.map((p) => <PartRow key={`${p.code}-${p.ref}`} part={p} />)
              )}
            </div>
          </div>
        </div>
      </aside>

      <TechnicianPanelOsFotosModal
        open={showFotos}
        onClose={() => setShowFotos(false)}
        numeroOs={os.os}
        equip={equip}
        serial={serial || '—'}
        fotos={fotos}
      />
    </>
  );
}
